import type { AxiosInstance } from "axios";
import { Loader2, Search } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { Badge } from "../components/ui/badge.js";
import { Input } from "../components/ui/input.js";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table.js";
import { apiErrorMessage } from "../lib/api.js";
import { fetchDirectory, isComped, type DirectoryOrg } from "../lib/admin.js";
import { StatusBadge } from "./status-badge.js";

/**
 * Every organization, searchable. This is the way in to an org's page, where a plan is
 * assigned or an org is comped. The list is bounded server-side (staff or not, an
 * unbounded directory is a page that times out during an incident).
 */
export function OrgsScreen({
  client,
  onOpenOrg,
}: {
  readonly client: AxiosInstance;
  readonly onOpenOrg: (orgId: string) => void;
}): React.JSX.Element {
  const [orgs, setOrgs] = useState<readonly DirectoryOrg[] | null>(null);
  const [search, setSearch] = useState("");
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (term: string) => {
      try {
        setOrgs(await fetchDirectory(client, term));
        setError(null);
      } catch (caught) {
        setError(apiErrorMessage(caught, "Could not load organizations."));
      }
    },
    [client],
  );

  // Debounced search: the directory refetches 300ms after the last keystroke.
  useEffect(() => {
    const timer = setTimeout(() => void load(search), 300);
    return () => clearTimeout(timer);
  }, [search, load]);

  return (
    <div className="flex w-full flex-col gap-4">
      <header className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">Organizations</h1>
          <p className="text-muted-foreground text-sm">Assign plans, comp accounts, inspect access.</p>
        </div>
        <div className="relative w-64">
          <Search className="text-muted-foreground absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
          <Input
            placeholder="Search name, client or shop…"
            className="h-9 pl-8"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </header>

      {error !== null && <p className="text-destructive text-sm">{error}</p>}

      {orgs === null ? (
        <p className="text-muted-foreground flex items-center gap-2 p-6 text-sm">
          <Loader2 className="size-4 animate-spin" /> Loading…
        </p>
      ) : orgs.length === 0 ? (
        <p className="text-muted-foreground p-6 text-sm">No organizations match.</p>
      ) : (
        <div className="bg-card overflow-x-auto rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Organization</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead className="text-right">Stores</TableHead>
                <TableHead className="text-right">Members</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orgs.map((org) => (
                <TableRow
                  key={org.id}
                  className="hover:bg-accent cursor-pointer"
                  onClick={() => onOpenOrg(org.id)}
                >
                  <TableCell>
                    <div className="font-medium">{org.name}</div>
                    <div className="text-muted-foreground text-xs">{org.billingEmail}</div>
                  </TableCell>
                  <TableCell className="capitalize">{org.kind}</TableCell>
                  <TableCell>
                    {org.subscription === null ? (
                      <Badge variant="outline" className="text-muted-foreground">
                        No plan
                      </Badge>
                    ) : (
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm">{org.subscription.planName}</span>
                        <StatusBadge status={org.subscription.status} />
                        {isComped(org.subscription) && (
                          <Badge className="border-transparent bg-emerald-600 text-white">Comp</Badge>
                        )}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{org.storeCount}</TableCell>
                  <TableCell className="text-right tabular-nums">{org.memberCount}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
